import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"
import { Label } from "@/components/ui/label"

const tisaInputVariants = cva(
  "input-child flex w-full border-2 bg-background px-4 text-child-base ring-offset-background file:border-0 file:bg-transparent file:text-child-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-4 disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "border-input focus-visible:border-primary focus-visible:ring-primary/20",
        error: "border-destructive focus-visible:border-destructive focus-visible:ring-destructive/20",
        success: "border-tisa-green-500 focus-visible:border-tisa-green-500 focus-visible:ring-tisa-green-500/20",
      },
      size: {
        sm: "h-11 px-3 text-child-sm rounded-lg",
        md: "h-14 px-4 text-child-base rounded-xl",
        lg: "h-16 px-6 text-child-lg rounded-xl",
        xl: "h-20 px-8 text-child-xl rounded-2xl",
      },
      readingAssist: {
        true: "font-mono tracking-wide",
        false: "",
      }
    },
    defaultVariants: {
      variant: "default",
      size: "md",
      readingAssist: false,
    },
  }
)

export interface TISAInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof tisaInputVariants> {
  label?: string
  helpText?: string
  errorText?: string
  icon?: React.ReactNode
  iconPosition?: "left" | "right"
  isRequired?: boolean
  showCharCount?: boolean
  maxLength?: number
}

const TISAInput = React.forwardRef<HTMLInputElement, TISAInputProps>(
  ({
    className,
    type,
    variant,
    size,
    readingAssist,
    label,
    helpText,
    errorText,
    icon,
    iconPosition = "left",
    isRequired = false,
    showCharCount = false,
    maxLength,
    value,
    id,
    ...props
  }, ref) => {
    const [charCount, setCharCount] = React.useState(0)
    const inputId = id || React.useId()

    // Update character count
    React.useEffect(() => {
      if (showCharCount && typeof value === 'string') {
        setCharCount(value.length)
      }
    }, [value, showCharCount])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (showCharCount) {
        setCharCount(e.target.value.length)
      }
      props.onChange?.(e)
    }

    // Determine variant based on error state
    const inputVariant = errorText ? "error" : variant

    return (
      <div className="space-y-2">
        {label && (
          <Label
            htmlFor={inputId}
            className="text-child-base font-medium text-foreground"
          >
            {label}
            {isRequired && (
              <span className="ml-1 text-destructive" aria-label="required">
                *
              </span>
            )}
          </Label>
        )}

        <div className="relative">
          {icon && iconPosition === "left" && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              {icon}
            </div>
          )}

          <input
            type={type}
            id={inputId}
            className={cn(
              tisaInputVariants({ variant: inputVariant, size, readingAssist }),
              icon && iconPosition === "left" && "pl-10",
              icon && iconPosition === "right" && "pr-10",
              className
            )}
            ref={ref}
            value={value}
            maxLength={maxLength}
            onChange={handleChange}
            aria-describedby={
              helpText || errorText ? `${inputId}-description` : undefined
            }
            aria-invalid={!!errorText}
            {...props}
          />

          {icon && iconPosition === "right" && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              {icon}
            </div>
          )}
        </div>

        <div className="flex justify-between">
          <div className="flex-1">
            {errorText && (
              <p
                id={`${inputId}-description`}
                className="text-child-sm text-destructive flex items-center gap-1"
              >
                <span className="text-destructive">⚠️</span>
                {errorText}
              </p>
            )}
            {helpText && !errorText && (
              <p
                id={`${inputId}-description`}
                className="text-child-sm text-muted-foreground"
              >
                {helpText}
              </p>
            )}
          </div>

          {showCharCount && maxLength && (
            <p className="text-child-xs text-muted-foreground">
              {charCount}/{maxLength}
            </p>
          )}
        </div>
      </div>
    )
  }
)
TISAInput.displayName = "TISAInput"

// PIN Input Component for Student Authentication
interface TISAPinInputProps {
  value: string
  onChange: (value: string) => void
  length?: number
  disabled?: boolean
  error?: boolean
  className?: string
}

const TISAPinInput = React.forwardRef<HTMLDivElement, TISAPinInputProps>(
  ({ value, onChange, length = 4, disabled = false, error = false, className }, ref) => {
    const inputs = Array.from({ length }, (_, i) => i)

    const handleChange = (index: number, digit: string) => {
      if (!/^\d?$/.test(digit)) return // Only allow digits

      const newValue = value.split('')
      newValue[index] = digit
      onChange(newValue.join('').slice(0, length))

      // Auto-focus next input
      if (digit && index < length - 1) {
        const nextInput = document.querySelector(
          `input[data-pin-index="${index + 1}"]`
        ) as HTMLInputElement
        nextInput?.focus()
      }
    }

    const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
      if (e.key === 'Backspace' && !value[index] && index > 0) {
        // Focus previous input on backspace
        const prevInput = document.querySelector(
          `input[data-pin-index="${index - 1}"]`
        ) as HTMLInputElement
        prevInput?.focus()
      }
    }

    return (
      <div ref={ref} className={cn("flex gap-3 justify-center", className)}>
        {inputs.map((index) => (
          <input
            key={index}
            data-pin-index={index}
            type="text"
            inputMode="numeric"
            pattern="[0-9]"
            maxLength={1}
            value={value[index] || ''}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            disabled={disabled}
            className={cn(
              "h-16 w-16 text-center text-2xl font-bold rounded-xl border-2 bg-background",
              "focus:outline-none focus:ring-4 focus:ring-offset-2",
              error
                ? "border-destructive focus:border-destructive focus:ring-destructive/20"
                : "border-input focus:border-primary focus:ring-primary/20",
              disabled && "opacity-50 cursor-not-allowed"
            )}
            aria-label={`PIN digit ${index + 1}`}
          />
        ))}
      </div>
    )
  }
)
TISAPinInput.displayName = "TISAPinInput"

// Text Area for Book Summaries
const TISATextarea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
    label?: string
    helpText?: string
    errorText?: string
    showCharCount?: boolean
    maxLength?: number
  }
>(({
  className,
  label,
  helpText,
  errorText,
  showCharCount = false,
  maxLength,
  value,
  id,
  ...props
}, ref) => {
  const [charCount, setCharCount] = React.useState(0)
  const textareaId = id || React.useId()

  React.useEffect(() => {
    if (showCharCount && typeof value === 'string') {
      setCharCount(value.length)
    }
  }, [value, showCharCount])

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (showCharCount) {
      setCharCount(e.target.value.length)
    }
    props.onChange?.(e)
  }

  return (
    <div className="space-y-2">
      {label && (
        <Label htmlFor={textareaId} className="text-child-base font-medium">
          {label}
        </Label>
      )}

      <textarea
        id={textareaId}
        className={cn(
          "flex min-h-[120px] w-full rounded-xl border-2 border-input bg-background px-4 py-3 text-child-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/20 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50",
          errorText && "border-destructive focus-visible:border-destructive focus-visible:ring-destructive/20",
          className
        )}
        ref={ref}
        value={value}
        maxLength={maxLength}
        onChange={handleChange}
        aria-describedby={
          helpText || errorText ? `${textareaId}-description` : undefined
        }
        aria-invalid={!!errorText}
        {...props}
      />

      <div className="flex justify-between">
        <div className="flex-1">
          {errorText && (
            <p
              id={`${textareaId}-description`}
              className="text-child-sm text-destructive flex items-center gap-1"
            >
              <span>⚠️</span>
              {errorText}
            </p>
          )}
          {helpText && !errorText && (
            <p
              id={`${textareaId}-description`}
              className="text-child-sm text-muted-foreground"
            >
              {helpText}
            </p>
          )}
        </div>

        {showCharCount && maxLength && (
          <p className="text-child-xs text-muted-foreground">
            {charCount}/{maxLength}
          </p>
        )}
      </div>
    </div>
  )
})
TISATextarea.displayName = "TISATextarea"

export { TISAInput, TISAPinInput, TISATextarea, tisaInputVariants }